import { useState, useEffect, type JSXElementConstructor } from 'react';
import { SchoolSearchBox } from './SchoolSearchBox';
import { SchoolSearchDetails } from './SchoolSearchDetails';
import { WithLocation } from './WithLocation';
import { LoadingEllipsis } from './LoadingEllipsis';
import { addCommas } from '../utils/numberUtils';
import { type GeoLocationPlace } from './location/types';

/**
 * Widget for searching for a school by name. Associated CSS is in `schoolRequest.scss`.
 *
 * Some code paths require the Google Places API. Use <templates:googlePlaces /> in your JSP to make that available.
 */

// This interface definition is incomplete.
interface SchoolSearchType {
    // Note that while this is technically optional and there are fallbacks within SchoolSearchBox etc. to geolocate
    // without this prop, the UX gets weird if you rely on those fallbacks; in particular, we don't seem to be able
    // to hide the ZIP input without this prop.
    location?: GeoLocationPlace,
    isTeacherRegistrationRequest?: boolean,
    userFullName?: string,
    userEmail?: string,
    setLocation: (location:GeoLocationPlace) => void,
    setSelectedSchool: (selectedSchool:number) => void,
    clearSelectedSchool: () => void,
    excludeTestRecords?: boolean,
    onSchoolSearchFocus?: () => void,
    setShowNewSchoolRequestForm?: () => void,
    // If you provide this argument, when you pick a school we'll render an instance of whatever component you
    // passed instead of rendering an instance of SchoolSearchDetails.
    // TODO: Once SchoolSearchDetails has an interface, it should be used here
    customSchoolSearchDetailsComponent?: JSXElementConstructor<any>,
    // See comment in ZipCodeSearch in SchoolSearchBox
    skipAutofocus: boolean,
    // If present, don't allow users to submit new schools and hide copy suggesting they can do that.
    blockNewRequestForm?: boolean,
}

export const SchoolSearch = ({
    customSchoolSearchDetailsComponent,
    location,
    isTeacherRegistrationRequest,
    userFullName,
    userEmail,
    setSelectedSchool,
    onSchoolSearchFocus,
    excludeTestRecords = true,
    clearSelectedSchool: clearSelectedSchoolProp,
    setLocation: setLocationProp,
    setShowNewSchoolRequestForm: setShowNewSchoolRequestFormProp,
    skipAutofocus,
    blockNewRequestForm,
}:SchoolSearchType) => {
    const [schoolDetails, setSchoolDetails] = useState(null)
    const [showSchoolSearchNote, setShowSchoolSearchNote] = useState(false)
    const [loadingSchoolDetails, setLoadingSchoolDetails] = useState(false)
    const [showNewSchoolRequestForm, setShowNewSchoolRequestFormState] = useState(false)
    const [confirmedShowNewSchoolRequestForm, setConfirmedShowNewSchoolRequestForm] = useState(false)
    const [showUpdateSchoolInfoForm, setShowUpdateSchoolInfoForm] = useState(false)
    const [showZipCodeSearch, setShowZipCodeSearch] = useState(!location)
    const [numTotalTeachers, setNumTotalTeachers] = useState<null|string>(null)
    const [loadingLocation, setLoadingLocation] = useState(false)


    useEffect(() => {
        $.getJSON(baseCacheURL + 'about/impact.json', (data:{stats:{teachersProjectsFunded:string}})=> {
            setNumTotalTeachers(addCommas(JSON.parse(data.stats.teachersProjectsFunded)));
        });
    }, []);

    const clearSelectedSchool = () => {
        clearSelectedSchoolProp();
        setSchoolDetails(null);
    }

    const getSchoolDetails = (schoolId:unknown) => {
        setLoadingSchoolDetails(true);
        $.ajax({
            url: '/school/school_details.html',
            data: { schoolId: schoolId },
            dataType: 'json',
        }).done((data) => {
            setSchoolDetails(data);
        }).fail(() => {
            clearSelectedSchool();
            alert('There was an error retrieving the school. Please try again.');
        }).always(() => setLoadingSchoolDetails(false));
    };

    const setLocation = (location:GeoLocationPlace) => {
        setLocationProp(location);
        setLoadingLocation(false);
        if (location) {
            setShowZipCodeSearch(false);
        }
    }

    // TODO conditionally break this
    const setShowNewSchoolRequestForm = (showNewSchoolRequestForm:boolean) => {
        if (setShowNewSchoolRequestFormProp) {
            setShowNewSchoolRequestFormProp();
        }
        else {
            setShowNewSchoolRequestFormState(showNewSchoolRequestForm)
        }
    }

    const searchZipCode = (zipCode:string, searchZipCodeDone:() => unknown) => {
        setLoadingLocation(true);
        $.ajax({
            url: '/common/zipLookup.html?zip=' + zipCode + '&APIKey=' + dcAPIKey,
            dataType: 'json',
        }).done((data) => {
            if (data) {
                setLocation({
                    zip: data.zip,
                    cityName: data.city,
                    stateCode: data.state,
                    latLng: { lat: data.latitude, lng: data.longitude },
                });
            } else {
                alert('There was an error retrieving this location. Please try again.');
                setLoadingLocation(false);
            }
            if (typeof searchZipCodeDone === 'function') {
                searchZipCodeDone();
            }
        }).fail(() => {
            alert('There was an error retrieving this location. Please try again.');
            setLoadingLocation(false);
        }).always(() => setShowZipCodeSearch(false));
    }

    const coreSchoolSearchBox = (
            <SchoolSearchBox
                    location={location}
                    isTeacherRegistrationRequest={isTeacherRegistrationRequest}
                    userFullName={userFullName}
                    userEmail={userEmail}
                    showZipCodeSearch={showZipCodeSearch}
                    showSchoolSearchNote={showSchoolSearchNote}
                    showNewSchoolRequestForm={!blockNewRequestForm && showNewSchoolRequestForm}
                    loadingLocation={loadingLocation}
                    confirmedShowNewSchoolRequestForm={!blockNewRequestForm && confirmedShowNewSchoolRequestForm}
                    getSchoolDetails={getSchoolDetails}
                    setShowZipCodeSearch={setShowZipCodeSearch}
                    searchZipCode={searchZipCode}
                    setShowSchoolSearchNote={setShowSchoolSearchNote}
                    setShowNewSchoolRequestForm={setShowNewSchoolRequestForm}
                    setSelectedSchool={setSelectedSchool}
                    setConfirmedShowNewSchoolRequestForm={setConfirmedShowNewSchoolRequestForm}
                    onSchoolSearchFocus={onSchoolSearchFocus}
                    setLocation={setLocation}
                    excludeTestRecords={excludeTestRecords}
                    skipAutofocus={skipAutofocus}
                    blockNewRequestForm={blockNewRequestForm}
            />
    );


    if (loadingSchoolDetails) {
        return (
                <div className="input-holder">
                    <LoadingEllipsis />
                </div>
        );
    } else if (schoolDetails) {
        const SchoolSearchDetailsComponent = customSchoolSearchDetailsComponent || SchoolSearchDetails;
        return (
                <div className="input-holder">
                    <SchoolSearchDetailsComponent
                            location={location}
                            isTeacherRegistrationRequest={isTeacherRegistrationRequest}
                            userFullName={userFullName}
                            userEmail={userEmail}
                            schoolDetails={schoolDetails}
                            numTotalTeachers={numTotalTeachers}
                            showUpdateSchoolInfoForm={showUpdateSchoolInfoForm}
                            setShowUpdateSchoolInfoForm={setShowUpdateSchoolInfoForm}
                            clearSelectedSchool={clearSelectedSchool}
                    />
                </div>
        );
    }

    if (!location) {
        return (
                <div className="input-holder">
                    <WithLocation setLocation={setLocation}>
                        {coreSchoolSearchBox}
                    </WithLocation>
                </div>
        );
    } else {
        return (
                <div className="input-holder">
                    {coreSchoolSearchBox}
                </div>
        );
    }
}
